import React from 'react';

import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Controller } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import useMediaQuery from '../../../hooks/useMediaQuery';

import { supportNumber } from '../../../utils/AnswerWritingUtil';

import styles from './Packages.module.scss';

function CoursePackages({ courseId, items, features }) {
  const [expandFeatures, setExpandFeatures] = React.useState(false);

  const [packageSwiper, setPackageSwiper] = React.useState();
  const [priceSwiper, setPriceSwiper] = React.useState();
  const [validitySwiper, setValiditySwiper] = React.useState();
  const [emiSwiper, setEmiSwiper] = React.useState();
  const [featuresSwiper, setFeaturesSwiper] = React.useState();

  const isMobile = useMediaQuery('(max-width: 30rem)');

  if (isMobile) {
    return (
      <div className={styles.wrapper}>
        <header className={styles.packagesHeader}>
          <div className={styles.headerRow}>
            <Swiper
              modules={[Controller]}
              slidesPerView={1.2}
              width={200}
              spaceBetween={15}
              onSwiper={setPackageSwiper}
              controller={{ control: [priceSwiper, validitySwiper, emiSwiper, featuresSwiper] }}
            >
              {items.map((item) => (
                <SwiperSlide key={item.package_id}>
                  <div className={classNames(styles.packageTitle, styles[`package${item.packageName}`])}>
                    {item.packageName}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <div className={styles.contentRow}>
            <div className={styles.title}>Price</div>

            <div className={styles.contentRowItems}>
              <Swiper slidesPerView={1} width={200} onSwiper={setPriceSwiper} allowTouchMove={false}>
                {items.map((item) => (
                  <SwiperSlide key={item.packageId}>
                    <div className={styles.packagePrice}>
                      <span className={styles.packageNetPrice}>
                        {'\u20B9'}
                        {item.packageBasePrice}
                      </span>
                      <span className={styles.packageSalePrice}>
                        {'\u20B9'}
                        {item.packageDiscountPrice2}
                      </span>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>

          <div className={styles.contentRow}>
            <div className={styles.title}>Validity</div>

            <div className={styles.contentRowItems}>
              <Swiper slidesPerView={1} width={200} onSwiper={setValiditySwiper} allowTouchMove={false}>
                {items.map((item) => (
                  <SwiperSlide key={item.packageId}>
                    <div key={item.packageId} className={styles.packageValidity}>
                      <span>{item.packageValidity} months</span>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>

          <div className={styles.contentRow}>
            <div className={styles.title}>EMI</div>

            <div className={styles.contentRowItems}>
              <Swiper slidesPerView={1} width={200} onSwiper={setEmiSwiper} allowTouchMove={false}>
                {items.map((item) => (
                  <SwiperSlide key={item.packageId}>
                    <div
                      key={item.packageId}
                      className={classNames(styles.packageEmiAvailable, {
                        [styles.haveEmiEnabled]: item?.isEmiAvailable,
                      })}
                    >
                      <span className={styles.emiIconWrapper}>
                        <FontAwesomeIcon icon={item?.isEmiAvailable === 0 ? faTimes : faCheck} />
                      </span>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </header>

        <div className={styles.needHelpWrapper}>
          <span>
            Need any help? Call <b>{supportNumber(courseId)?.dash}</b>
          </span>
        </div>

        <h3 className={styles.featuresTitle}>Features</h3>

        <div className={styles.featuresList}>
          <Swiper
            modules={[Controller]}
            slidesPerView={1}
            onSwiper={setFeaturesSwiper}
            controller={{ control: packageSwiper }}
          >
            {items.map((item) => (
              <SwiperSlide key={item.packageId}>
                {features.slice(0, expandFeatures ? features.length : 5).map((feature) => (
                  <div key={feature.featureId} className={styles.contentRow}>
                    <div className={styles.title}>
                      <span className={styles.featureTitle}>{feature?.featureName}</span>
                      <span className={styles.featureDesc}>{feature?.featureDescription}</span>
                    </div>

                    <div className={styles.contentRowItems}>
                      <div
                        key={item.packageId}
                        className={classNames(styles.packageFeature, {
                          [styles.isAvailable]: item.features.includes(feature.featureId),
                        })}
                      >
                        <span className={styles.iconWrapper}>
                          <FontAwesomeIcon icon={item.features.includes(feature.featureId) ? faCheck : faTimes} />
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </SwiperSlide>
            ))}
          </Swiper>

          {features.length > 5 && !expandFeatures && (
            <div className={styles.expandFeatures} onClick={() => setExpandFeatures((prev) => !prev)}>
              <span>Expand Features</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <header className={styles.packagesHeader}>
        <div className={styles.headerRow}>
          {items.map((item) => (
            <div
              key={item.package_id}
              className={classNames(styles.packageTitle, styles[`package${item.packageName}`])}
            >
              {item.packageName}
            </div>
          ))}
        </div>

        <div className={styles.contentRow}>
          <div className={styles.title}>Prices</div>

          <div className={styles.contentRowItems}>
            {items.map((item) => (
              <div key={item.package_id} className={styles.packagePrice}>
                <span className={styles.packageNetPrice}>
                  {'\u20B9'}
                  {item.packageBasePrice}
                </span>
                <span className={styles.packageSalePrice}>
                  {'\u20B9'}
                  {item.packageDiscountPrice2}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.contentRow}>
          <div className={styles.title}>Validity</div>

          <div className={styles.contentRowItems}>
            {items.map((item) => (
              <div key={item.packageId} className={styles.packageValidity}>
                <span>{item.packageValidity} months</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.contentRow}>
          <div className={styles.title}>EMI</div>

          <div className={styles.contentRowItems}>
            {items.map((item) => (
              <div
                key={item.package_id}
                className={classNames(styles.packageEmiAvailable, { [styles.haveEmiEnabled]: item.isEmiAvailable })}
              >
                <span className={styles.emiIconWrapper}>
                  <FontAwesomeIcon icon={item.isEmiAvailable === 0 ? faTimes : faCheck} />
                </span>
              </div>
            ))}
          </div>
        </div>
      </header>

      <div className={styles.needHelpWrapper}>
        <span>
          Need any help? Call <b>{supportNumber(courseId)?.dash}</b>
        </span>
      </div>

      <h3 className={styles.featuresTitle}>Features</h3>

      <div className={styles.featuresList}>
        {features.slice(0, expandFeatures ? features.length : 5).map((feature) => (
          <div key={feature.featureId} className={styles.contentRow}>
            <div className={styles.title}>
              <span className={styles.featureTitle}>{feature.featureName}</span>
              <span className={styles.featureDesc}>{feature.featureDescription}</span>
            </div>

            <div className={styles.contentRowItems}>
              {items.map((item) => (
                <div
                  key={item.packageId}
                  className={classNames(styles.packageFeature, {
                    [styles.isAvailable]: item.features.includes(feature.featureId),
                  })}
                >
                  <span className={styles.iconWrapper}>
                    <FontAwesomeIcon icon={item.features.includes(feature.featureId) ? faCheck : faTimes} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {features.length > 5 && !expandFeatures && (
          <div className={styles.expandFeatures} onClick={() => setExpandFeatures((prev) => !prev)}>
            <span>Expand Features</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoursePackages;
